/**
 * REST contract for `GET /api/study-groups/:groupId/leaderboard`.
 *
 * Shared makes no HTTP calls — by design — but it DOES own the URL
 * path + response schema so the wire contract between server and
 * clients lives in one file:
 *
 *   - `LEADERBOARD_PATH(groupId)` — `/study-groups/{encoded}/leaderboard`
 *     (the relative path; clients prepend their own API base / RPC root).
 *   - `leaderboardResponseSchema` — Zod parser run on the raw JSON so
 *     a wire-shape drift between server and client surfaces at the
 *     boundary instead of slipping through as silent `undefined.userId`
 *     downstream in the row renderer.
 *
 * Pairs with `LeaderboardResponse` / `LeaderboardRow` / `LeaderboardPeriod`
 * exported from `./scoring`; the inferred types here intentionally
 * match those shapes one-for-one (and `.parse()` returns a value
 * assignable to `LeaderboardResponse`).
 */

import { z } from 'zod';

/** REST period query enum, matches `LeaderboardPeriod` in scoring.ts. */
export const leaderboardPeriodSchema = z.enum([
  '1week',
  '1month',
  '3months',
  '6months',
  'alltime',
]);

/** Single ranking row in the REST response. Display MP (centi-MP / 100,
 *  Math.round) — see `buildLeaderboard`. */
export const leaderboardRowSchema = z.object({
  userId: z.string(),
  rank: z.number().int().nonnegative(),
  mp: z.number().int().nonnegative(),
  streak: z.number().int().nonnegative(),
  hiddenSelf: z.boolean(),
  displayName: z.string().optional(),
  avatarUrl: z.string().optional(),
  lastActive: z.string().optional(),
  /** Tracked study time for the period, raw integer ms. OPTIONAL for
   *  rollout skew (old server → new client); consumers fall back to 0. */
  timeMs: z.number().int().nonnegative().optional(),
  /** Dense rank within the independent Time ranking. Same skew contract. */
  timeRank: z.number().int().nonnegative().optional(),
});

/** Full response. `success: true` is the success-envelope flag the Hono
 *  server adds; classification of success vs error happens BEFORE this
 *  schema runs (clients check `success === true` first), so the schema
 *  itself is lenient about it. */
export const leaderboardResponseSchema = z.object({
  period: leaderboardPeriodSchema,
  ranking: z.array(leaderboardRowSchema),
  requestor: leaderboardRowSchema,
});

/** REST relative path. Server route lives under `/api/study-groups/...`
 *  on web (Hono) — the `/api` prefix is the client's transport concern,
 *  this constant carries only the resource path. */
export function LEADERBOARD_PATH(groupId: string): string {
  return `/study-groups/${encodeURIComponent(groupId)}/leaderboard`;
}
